import React, { useCallback, useEffect, useState, useRef, lazy } from 'react';
import { ArrowRight, Link as LinkIcon, Zap } from 'lucide-react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { X } from 'lucide-react';
interface TimelineItem {
  id: number;
  title: string;
  date: string;
  content: string;
  category: string;
  icon: React.ElementType;
  relatedIds: number[];
  status: 'completed' | 'in-progress' | 'pending';
  energy: number;
}
interface RadialOrbitalTimelineProps {
  timelineData: TimelineItem[];
}
export function RadialOrbitalTimeline({
  timelineData
}: RadialOrbitalTimelineProps) {
  const [expandedItems, setExpandedItems] = useState<Record<number, boolean>>({});
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [autoRotate, setAutoRotate] = useState<boolean>(true);
  const [pulseEffect, setPulseEffect] = useState<Record<number, boolean>>({});
  const [activeNodeId, setActiveNodeId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const orbitRef = useRef<HTMLDivElement>(null);
  const nodeRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);
  const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === containerRef.current || e.target === orbitRef.current) {
      setExpandedItems({});
      setActiveNodeId(null);
      setPulseEffect({});
      setAutoRotate(true);
    }
  }, []);
  const toggleItem = useCallback((id: number) => {
    setExpandedItems(prev => {
      const newState = {
        ...prev
      };
      Object.keys(newState).forEach(key => {
        if (parseInt(key) !== id) {
          newState[parseInt(key)] = false;
        }
      });
      newState[id] = !prev[id];
      if (!prev[id]) {
        setActiveNodeId(id);
        setAutoRotate(false);
        // Calculate the current angle of the clicked item
        const itemIndex = timelineData.findIndex(item => item.id === id);
        const currentAngle = (itemIndex / timelineData.length * 360 + rotationAngle) % 360;
        // Set rotation to position the item at the top (270 degrees)
        setRotationAngle(prev => prev - (currentAngle - 270));
        const relatedItems = getRelatedItems(id);
        const newPulseEffect: Record<number, boolean> = {};
        relatedItems.forEach(relId => {
          newPulseEffect[relId] = true;
        });
        setPulseEffect(newPulseEffect);
      } else {
        setActiveNodeId(null);
        setAutoRotate(true);
        setPulseEffect({});
      }
      return newState;
    });
  }, [timelineData, rotationAngle]);
  useEffect(() => {
    let rotationTimer: NodeJS.Timeout | null = null;
    if (autoRotate) {
      rotationTimer = setInterval(() => {
        setRotationAngle(prev => (prev + 0.3) % 360);
      }, 50);
    }
    return () => {
      if (rotationTimer) {
        clearInterval(rotationTimer);
      }
    };
  }, [autoRotate]);
  const calculateNodePosition = useCallback((index: number, total: number) => {
    const angle = (index / total * 360 + rotationAngle) % 360;
    // Adjust radius based on screen size
    const radius = isMobile ? 90 : 120;
    const radian = angle * Math.PI / 180;
    const x = radius * Math.cos(radian);
    const y = radius * Math.sin(radian);
    const zIndex = Math.round(100 + 50 * Math.cos(radian));
    const opacity = 1;
    return {
      x,
      y,
      angle,
      zIndex,
      opacity
    };
  }, [rotationAngle, isMobile]);
  const getRelatedItems = useCallback((itemId: number): number[] => {
    const currentItem = timelineData.find(item => item.id === itemId);
    return currentItem ? currentItem.relatedIds : [];
  }, [timelineData]);
  const isRelatedToActive = useCallback((itemId: number): boolean => {
    if (!activeNodeId) return false;
    const relatedItems = getRelatedItems(activeNodeId);
    return relatedItems.includes(itemId);
  }, [activeNodeId, getRelatedItems]);
  return <div className="w-full flex flex-col items-center justify-center" ref={containerRef} onClick={handleContainerClick} data-id="element-126">
      {/* Orbit Animation */}
      <div className="relative h-[300px] w-full flex items-center justify-center mb-8" ref={orbitRef} data-id="element-127">
        <div className="absolute inset-0 flex items-center justify-center" data-id="element-128">
          <div className="absolute w-12 h-12 sm:w-16 sm:h-16 animate-pulse flex items-center justify-center z-10" data-id="element-129">
            <div className="absolute w-16 h-16 sm:w-24 sm:h-24 rounded-full border border-[#4ECDC4]/20 animate-ping opacity-70" data-id="element-130"></div>
            <div className="flex items-center justify-center h-8 sm:h-10 w-auto" data-id="element-131">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/oe4oUgDYZPX1RVXuLZNFcT/Chiro_Venture_-_white_no_background.png" alt="ChiroVenture Logo" className="h-8 sm:h-10 w-auto object-contain" data-id="element-132" />
            </div>
          </div>
          <div className="absolute w-[160px] h-[160px] sm:w-[200px] sm:h-[200px] rounded-full border border-[#4ECDC4]/10" data-id="element-133"></div>
          {timelineData.map((item, index) => {
          const position = calculateNodePosition(index, timelineData.length);
          const isExpanded = expandedItems[item.id];
          const Icon = item.icon;
          return <div key={item.id} ref={el => nodeRefs.current[item.id] = el} className="absolute transition-all duration-700 cursor-pointer" style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            zIndex: isExpanded ? 200 : position.zIndex
          }} onClick={e => {
            e.stopPropagation();
            toggleItem(item.id);
          }} data-id="element-134">
                <div className={`
                    w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center
                    ${isExpanded ? 'bg-[#4ECDC4] text-white border-[#4ECDC4]' : 'bg-gray-900 text-[#4ECDC4] border-gray-400'}
                    border-2 
                    transition-all duration-300
                    hover:bg-[#4ECDC4] hover:text-white hover:border-[#4ECDC4]
                  `} data-id="element-135">
                  <Icon size={isMobile ? 12 : 14} data-id="element-136" />
                </div>
              </div>;
        })}
        </div>
      </div>
      {/* List Items Container */}
      <div className="w-full relative" data-id="element-137">
        {/* List Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2" data-id="element-138">
          {timelineData.map(item => <div key={item.id} className={`flex items-center gap-2 p-1.5 rounded-lg transition-colors cursor-pointer
                ${activeNodeId === item.id ? 'bg-gray-800/50' : 'hover:bg-gray-800/30'}`} onClick={() => toggleItem(item.id)} data-id="element-139">
              <div className={`
                  w-6 h-6 sm:w-7 sm:h-7 rounded-full flex items-center justify-center transition-colors
                  ${activeNodeId === item.id ? 'bg-[#4ECDC4] text-white border-[#4ECDC4]' : 'bg-gray-900 text-[#4ECDC4] border-gray-400'}
                  border-2 
                  hover:bg-[#4ECDC4] hover:text-white hover:border-[#4ECDC4]
                `} data-id="element-140">
                <item.icon size={isMobile ? 12 : 14} data-id="element-141" />
              </div>
              <div data-id="element-142">
                <h3 className="text-xs sm:text-sm font-semibold text-white" data-id="element-143">
                  {item.title}
                </h3>
              </div>
            </div>)}
        </div>
        {/* Expanded Card Overlay - Now contained within the list section */}
        {Object.entries(expandedItems).map(([id, isExpanded]) => {
        if (!isExpanded) return null;
        const item = timelineData.find(t => t.id === parseInt(id));
        if (!item) return null;
        return <div key={`expanded-${id}`} className="absolute top-0 left-0 right-0 bottom-0 bg-gray-900/95 backdrop-blur-sm rounded-xl border border-gray-800 shadow-xl" onClick={e => e.stopPropagation()} data-id="element-144">
              <div className="p-4 relative h-full" data-id="element-145">
                <div className="flex items-start justify-between mb-4" data-id="element-146">
                  <div className="flex items-center gap-3" data-id="element-147">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center bg-[#4ECDC4] text-white border-2 border-[#4ECDC4]" data-id="element-148">
                      <item.icon size={16} data-id="element-149" />
                    </div>
                    <div data-id="element-150">
                      <h3 className="text-base font-light tracking-normal text-white" data-id="element-151">
                        {item.title}
                      </h3>
                      <p className="text-xs text-gray-400 font-light" data-id="element-152">
                        {item.category}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => toggleItem(item.id)} className="p-2 hover:bg-white/10 rounded-full transition-colors" data-id="element-153">
                    <X className="w-5 h-5" data-id="element-154" />
                  </button>
                </div>
                <div className="overflow-y-auto max-h-[calc(100%-4rem)]" data-id="element-155">
                  <p className="text-sm text-gray-300 leading-relaxed font-light" data-id="element-156">
                    {item.content}
                  </p>
                </div>
              </div>
            </div>;
      })}
      </div>
    </div>;
}